import { z } from "zod";
import { SuccessMetaSchema } from "../common";

export const OtpChannelEnum = z.enum([
  "whatsapp",
  "sms",
  "whatsapp_fallback_sms",
]);

// =========================================================================
// POST /auth/otp/send
// =========================================================================

export const SendOtpRequestSchema = z.object({
  phone: z
    .string()
    .regex(/^\+[1-9]\d{7,14}$/, "El número debe estar en formato E.164"),
  shop_domain: z
    .string()
    .min(1)
    .regex(/\.myshopify\.com$/, "Invalid Shopify domain"),
});

export const SendOtpResponseSchema = z.object({
  meta: SuccessMetaSchema,
  request_id: z.string().uuid(),
  /** Canal efectivo usado para este envío (relevante en whatsapp_fallback_sms). */
  channel_used: OtpChannelEnum,
  retry_after_seconds: z.number().int().nonnegative(),
  /**
   * `true` cuando el número ya fue verificado dentro de `validity_period_hours`
   * y el backend omitió el envío, devolviendo directamente un `verification_token`.
   */
  skipped: z.boolean().default(false),
  /** Solo presente cuando `skipped === true`. */
  verification_token: z.string().optional(),
});

// =========================================================================
// POST /auth/otp/verify
// =========================================================================

export const VerifyOtpRequestSchema = z.object({
  request_id: z.string().uuid(),
  code: z
    .string()
    .regex(
      /^\d{4,8}$/,
      "El código debe ser numérico y tener entre 4 y 8 dígitos"
    ),
});

export const VerifyOtpResponseSchema = z.object({
  meta: SuccessMetaSchema,
  /**
   * JWT de corta duración firmado por el backend.
   * El frontend lo adjunta como Bearer token en el header
   * `Authorization` al llamar a POST /orders.
   *
   * Tiempo de vida recomendado: 10 min (configurable por env var).
   */
  verification_token: z.string().min(1),
});

// =========================================================================
// POST /admin/otp-config/test-send
// POST /admin/otp-config/test-verify
//
// Admin-only equivalents of /auth/otp/send and /auth/otp/verify, used by the
// "send a real test OTP" flow in the config page. `shop_domain` comes from the
// verified Shopify Admin session rather than the request body.
// =========================================================================

export const TestSendOtpRequestSchema = z.object({
  phone: SendOtpRequestSchema.shape.phone,
});

export const TestSendOtpResponseSchema = z.object({
  meta: SuccessMetaSchema,
  request_id: z.string().uuid(),
  channel_used: OtpChannelEnum,
  retry_after_seconds: z.number().int().nonnegative(),
  /**
   * Unlike `SendOtpResponseSchema`, this never carries a `verification_token`
   * even when `skipped === true` — the admin test flow always wants the
   * merchant to exercise the verify step manually, so it deliberately
   * doesn't short-circuit here. Not an oversight.
   */
  skipped: z.boolean().default(false),
  /** Costo real ya deducido del saldo por este envío (0 si `skipped`). */
  actual_cost_usd: z.number().nonnegative(),
});

export const TestVerifyOtpRequestSchema = z.object({
  request_id: z.string().uuid(),
  code: VerifyOtpRequestSchema.shape.code,
});

export const TestVerifyOtpResponseSchema = z.object({
  meta: SuccessMetaSchema,
  ok: z.literal(true),
});

// =========================================================================
// ERROR CODES
// =========================================================================

export const OtpErrorCodeEnum = z.enum([
  "INVALID_CODE",
  "CODE_EXPIRED",
  "MAX_ATTEMPTS",
  "REQUEST_NOT_FOUND",
  "SHOP_NOT_FOUND",
  "RATE_LIMITED",
  "DELIVERY_FAILED",
  "OTP_DISABLED",
  "INVALID_PHONE_REGION",
  "INSUFFICIENT_BALANCE",
  "VOIP_BLOCKED",
]);

export const OtpErrorResponseSchema = z.object({
  error: z.object({ code: OtpErrorCodeEnum, message: z.string() }),
  request_id: z.string(),
  trace_id: z.string(),
  timestamp: z.iso.datetime(),
});

// =========================================================================
// POLICY CONSTANTS
// =========================================================================

export const OTP_CODE_LENGTH = 6;
export const OTP_RESEND_COOLDOWN_SECONDS = 60;
/** TTL del código OTP en sí (independiente de `OtpConfig.validity_period_hours`, que controla la ventana de "saltar OTP si ya verificado"). */
export const OTP_CODE_TTL_MINUTES = 5;
export const OTP_VERIFICATION_TOKEN_TTL_MINUTES = 10;
/** Twilio Lookup v2 line_type_intelligence cost per call */
export const OTP_LOOKUP_COST_USD = 0.005;

// =========================================================================
// TYPE EXPORTS
// =========================================================================

export type OtpChannel = z.infer<typeof OtpChannelEnum>;
export type OtpErrorCode = z.infer<typeof OtpErrorCodeEnum>;
export type OtpErrorResponse = z.infer<typeof OtpErrorResponseSchema>;
export type SendOtpRequest = z.infer<typeof SendOtpRequestSchema>;
export type SendOtpResponse = z.infer<typeof SendOtpResponseSchema>;
export type VerifyOtpRequest = z.infer<typeof VerifyOtpRequestSchema>;
export type VerifyOtpResponse = z.infer<typeof VerifyOtpResponseSchema>;
export type TestSendOtpRequest = z.infer<typeof TestSendOtpRequestSchema>;
export type TestSendOtpResponse = z.infer<typeof TestSendOtpResponseSchema>;
export type TestVerifyOtpRequest = z.infer<typeof TestVerifyOtpRequestSchema>;
export type TestVerifyOtpResponse = z.infer<typeof TestVerifyOtpResponseSchema>;
